// Compacte uurtijdlijn naast de dagkolom: Outlook-afspraken op hun tijd, de
// taken van vandaag als blokken. Een taak die je hierheen sleept krijgt een vaste
// begintijd (tijdblok); de rest staat als voorstel in planvolgorde in de gaten.
import React, { useEffect, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { KLEUR, MAAT } from './DagTaakRij';
import { fmtUur, fmtDuur, korteTitel, WERKDAG } from './dagplanning';

export const TL_VAN = 8;
export const TL_TOT = 19;
export const TL_PXH = 60; // px per uur — een half uur is hoog genoeg voor titel + tijd

// Overlappende items naast elkaar (kolom-index + aantal kolommen per cluster).
function naastElkaar(items) {
  const sorted = [...items].sort((a, b) => a.s - b.s || a.e - b.e);
  const uit = [];
  let cluster = [];
  let clusterEind = -Infinity;
  const flush = () => {
    const kolomEind = [];
    cluster.forEach((it) => {
      let k = kolomEind.findIndex((e) => it.s >= e - 1e-9);
      if (k === -1) { k = kolomEind.length; kolomEind.push(it.e); } else kolomEind[k] = it.e;
      it.kol = k;
    });
    cluster.forEach((it) => { it.kols = kolomEind.length; uit.push(it); });
    cluster = [];
  };
  sorted.forEach((it) => {
    if (cluster.length && it.s >= clusterEind - 1e-9) { flush(); clusterEind = -Infinity; }
    cluster.push(it);
    clusterEind = Math.max(clusterEind, it.e);
  });
  flush();
  return uit;
}

function TaakBlok({ blok, top, hoogte, links, breedte, onKlik, eindY }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tijdlijn:${blok.id}`, data: { item: blok.item, bron: 'tijdlijn' },
  });
  const titel = korteTitel(blok.item.title, blok.item.projectName);
  // Deel na het einde van de werkdag: rood gearceerd.
  const overVanaf = blok.over ? Math.max(0, eindY - top) : null;
  const uitleg = blok.over ? ' — loopt na het einde van je werkdag' : blok.vast ? '' : ' (voorstel — sleep om vast te zetten)';
  return (
    <Tooltip title={`${fmtUur(blok.s)}–${fmtUur(blok.e)} · ${blok.item.title}${uitleg}`} placement="left" enterDelay={300}>
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onClick={(e) => onKlik?.(blok.item, e)}
        sx={{
          position: 'absolute', top: top + 1, height: Math.max(12, hoogte - 3), left: links, width: breedte,
          borderRadius: '6px', px: 0.75, py: '2px', overflow: 'hidden', cursor: 'grab', touchAction: 'none',
          backgroundColor: blok.vast ? KLEUR.primairVlak : '#f4f7fb',
          border: blok.vast ? '1px solid transparent' : '1px dashed #c9d3e0',
          opacity: isDragging ? 0.4 : 1, zIndex: 2,
        }}
      >
        {overVanaf != null && (
          <Box sx={{
            position: 'absolute', left: 0, right: 0, top: overVanaf, bottom: 0, pointerEvents: 'none',
            background: 'repeating-linear-gradient(135deg, #fce8e8 0 5px, #fdf3f3 5px 10px)',
          }} />
        )}
        <Typography sx={{ position: 'relative', fontSize: MAAT.klein, fontWeight: 500, color: KLEUR.tekst, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {titel}
        </Typography>
        {hoogte >= 26 && (
          <Typography sx={{ position: 'relative', fontSize: MAAT.klein, color: blok.over ? KLEUR.telaat : KLEUR.zacht, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
            {fmtUur(blok.s)} · {fmtDuur((blok.e - blok.s) * 60)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/**
 * afspraken: [{ id, titel, s, e, vrij, item? }] — item = gekoppelde Airtable-taak (klikbaar)
 * blokken:   [{ id, s, e, vast, over, item }]
 */
export default function DagTijdlijn({ afspraken, blokken, nu, onKlikTaak, meetRef, agendaFout }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'tijdlijn' });
  const scrollRef = useRef(null);
  const hoogte = (TL_TOT - TL_VAN) * TL_PXH;
  const y = (h) => (Math.max(TL_VAN, Math.min(TL_TOT, h)) - TL_VAN) * TL_PXH;

  // Bij openen naar "nu" (of begin werkdag) scrollen.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const doel = nu != null && nu > WERKDAG.start ? nu - 1 : WERKDAG.start - 0.5;
    el.scrollTop = Math.max(0, y(doel));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = naastElkaar([
    ...afspraken.map((a) => ({ ...a, soort: 'afspraak' })),
    ...blokken.map((b) => ({ ...b, soort: 'taak' })),
  ]);

  const uren = [];
  for (let h = TL_VAN; h <= TL_TOT; h += 1) uren.push(h);
  const zetRef = (el) => { setNodeRef(el); if (meetRef) meetRef.current = el; };

  return (
    <Box ref={scrollRef} sx={{ maxHeight: { md: 'calc(100vh - 230px)' }, minHeight: 280, overflowY: 'auto', pr: 0.5 }}>
      <Box ref={zetRef} sx={{
        position: 'relative', height: hoogte, ml: '40px', borderRadius: '6px',
        backgroundColor: isOver ? '#eef2f7' : 'transparent', transition: 'background-color .15s',
      }}>
        {/* Werkdag-achtergrond */}
        {uren.map((h) => (
          <Box key={h} sx={{ position: 'absolute', left: 0, right: 0, top: y(h), borderTop: `1px solid ${KLEUR.lijn}` }}>
            <Typography sx={{ position: 'absolute', left: -40, top: -8, width: 34, textAlign: 'right', fontSize: MAAT.klein, color: KLEUR.stil, fontVariantNumeric: 'tabular-nums' }}>
              {String(h).padStart(2, '0')}:00
            </Typography>
          </Box>
        ))}
        {items.map((it) => {
          const top = y(it.s);
          const h = Math.max(12, y(it.e) - y(it.s));
          const breedte = `calc(${100 / it.kols}% - 3px)`;
          const links = `calc(${(100 / it.kols) * it.kol}% + 1px)`;
          if (it.soort === 'taak') {
            return <TaakBlok key={`t-${it.id}`} blok={it} top={top} hoogte={h} links={links} breedte={breedte} onKlik={onKlikTaak} eindY={y(WERKDAG.eind)} />;
          }
          return (
            <Tooltip key={`a-${it.id}`} title={`${fmtUur(it.s)}–${fmtUur(it.e)} · ${it.titel}${it.vrij ? ' (als vrij in je agenda)' : ''}`} placement="left" enterDelay={300}>
              <Box
                onClick={(e) => it.item && onKlikTaak?.(it.item, e)}
                sx={{
                  position: 'absolute', top: top + 1, height: Math.max(10, h - 3), left: links, width: breedte,
                  borderRadius: '6px', px: 0.75, py: '2px', overflow: 'hidden', zIndex: 1,
                  cursor: it.item ? 'pointer' : 'default',
                  backgroundColor: it.vrij ? 'transparent' : '#eff1f4',
                  border: it.vrij ? '1px dashed #d5dae0' : 'none',
                }}
              >
                <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.titel}
                </Typography>
                {h >= 26 && (
                  <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.stil, lineHeight: 1.2 }}>
                    {fmtUur(it.s)}–{fmtUur(it.e)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
        {/* Einde werkdag */}
        <Box sx={{ position: 'absolute', left: -6, right: 0, top: y(WERKDAG.eind), borderTop: `1px solid ${KLEUR.zacht}`, zIndex: 3, pointerEvents: 'none' }}>
          {/* Label in de goot links (onder het uurlabel), zodat het nooit over een blok valt */}
          <Typography sx={{ position: 'absolute', left: -34, top: 6, width: 34, textAlign: 'right', pr: '6px', fontSize: MAAT.klein, color: KLEUR.zacht, lineHeight: 1.1 }}>
            einde
          </Typography>
        </Box>
        {nu != null && nu >= TL_VAN && nu <= TL_TOT && (
          <Box sx={{ position: 'absolute', left: -6, right: 0, top: y(nu), borderTop: `1.5px solid ${KLEUR.primair}`, zIndex: 3, pointerEvents: 'none' }}>
            <Box sx={{ position: 'absolute', left: 0, top: -4, width: 7, height: 7, borderRadius: '50%', backgroundColor: KLEUR.primair }} />
          </Box>
        )}
      </Box>
      {agendaFout && (
        <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, mt: 1, ml: '40px' }}>
          Outlook-agenda niet geladen — alleen afspraken uit Airtable staan erin.
        </Typography>
      )}
    </Box>
  );
}
